import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TrendingDown, Scan, Trash2, CheckCircle, AlertTriangle, Loader2, Search, X } from 'lucide-react';
import api from '../../services/api';
import { OBRAS, fmt } from './insumos.utils';

export default function InsumosSaida() {
  const qc = useQueryClient();
  const barcodeRef = useRef();

  const [obra, setObra] = useState('');
  const [localizacaoId, setLocalizacaoId] = useState('');
  const [responsavelNome, setResponsavelNome] = useState('');
  const [funcionarioId, setFuncionarioId] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const [barcodeInput, setBarcodeInput] = useState('');
  const [itens, setItens] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');

  const { data: localizacoes } = useQuery({ queryKey: ['insumos-locs', obra], queryFn: () => obra ? api.get('/insumos/localizacoes', { params: { obra } }).then(r => r.data.data) : Promise.resolve([]), enabled: !!obra });
  const { data: funcionarios } = useQuery({ queryKey: ['estoque-funcionarios'], queryFn: () => api.get('/estoque/funcionarios').then(r => r.data.data) });
  const { data: saldos } = useQuery({ queryKey: ['insumos-saldo', obra], queryFn: () => obra ? api.get('/insumos/saldo', { params: { obra } }).then(r => r.data.data) : Promise.resolve([]), enabled: !!obra });

  // Focus barcode input on mount
  useEffect(() => { if (barcodeRef.current) barcodeRef.current.focus(); }, [obra]);

  async function buscarMaterial(q) {
    if (!q.trim()) return;
    setBuscando(true); setErroBusca('');
    try {
      const { data } = await api.get('/insumos/materiais/buscar', { params: { q: q.trim() } });
      const mat = data.data;

      // Check if already in list
      if (itens.find(i => i.material_id === mat.id)) {
        setBarcodeInput('');
        return;
      }

      // Check saldo for selected obra
      const saldoObra = saldos?.find(s => s.material_id === mat.id);
      itens.push({
        material_id: mat.id,
        material_nome: mat.nome,
        material_codigo: mat.codigo,
        unidade_sigla: mat.unidade_sigla || 'UN',
        barcode_lido: q.trim(),
        saldo_disponivel: parseFloat(saldoObra?.saldo || 0),
        quantidade: 1,
        unidade_id: mat.unidade_estoque_id,
      });
      setItens([...itens]);
      setBarcodeInput('');
    } catch (e) {
      setErroBusca(`"${q}" não encontrado no catálogo`);
    } finally { setBuscando(false); }
  }

  function onBarcodeKeyDown(e) {
    if (e.key === 'Enter') {
      buscarMaterial(barcodeInput);
    }
  }

  function setQtd(idx, val) {
    setItens(its => its.map((it, i) => i === idx ? { ...it, quantidade: val } : it));
  }

  function removerItem(idx) {
    setItens(its => its.filter((_, i) => i !== idx));
  }

  async function confirmarSaida() {
    if (!obra || !responsavelNome || !itens.length) return;
    // Validate stock
    const semSaldo = itens.filter(i => parseFloat(i.quantidade) > i.saldo_disponivel);
    if (semSaldo.length) {
      setErroEnvio(`Saldo insuficiente para: ${semSaldo.map(i => i.material_nome).join(', ')}`);
      return;
    }
    setLoading(true); setErroEnvio('');
    try {
      await api.post('/insumos/saidas', {
        obra,
        localizacao_id: localizacaoId || null,
        funcionario_id: funcionarioId || null,
        responsavel_nome: responsavelNome,
        observacoes,
        itens: itens.map(i => ({
          material_id: i.material_id,
          quantidade: i.quantidade,
          unidade_id: i.unidade_id,
          barcode_lido: i.barcode_lido,
        })),
      });
      setSucesso(true);
      qc.invalidateQueries(['insumos-saldo']);
      qc.invalidateQueries(['insumos-dashboard']);
    } catch (e) {
      setErroEnvio(e.response?.data?.error || 'Erro ao registrar saída');
    } finally { setLoading(false); }
  }

  function reset() {
    setItens([]); setSucesso(false); setErroEnvio(''); setObservacoes('');
  }

  if (sucesso) return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex flex-col items-center py-16 gap-6">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle size={40} className="text-green-500" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Saída registrada!</h2>
          <p className="text-gray-500">Estoque atualizado com sucesso.</p>
        </div>
        <button onClick={reset} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">
          Nova Saída
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <TrendingDown size={22} className="text-blue-600" />
        <h1 className="text-xl font-bold text-gray-800">Saída de Materiais</h1>
      </div>

      {/* Setup */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Informações da Saída</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Obra *</label>
            <select value={obra} onChange={e => { setObra(e.target.value); setLocalizacaoId(''); }}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar obra...</option>
              {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Localização</label>
            <select value={localizacaoId} onChange={e => setLocalizacaoId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" disabled={!obra}>
              <option value="">Sem localização</option>
              {(localizacoes || []).map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Funcionário / Empreiteiro</label>
            <select value={funcionarioId} onChange={e => {
              setFuncionarioId(e.target.value);
              const f = (funcionarios || []).find(x => String(x.id) === e.target.value);
              if (f) setResponsavelNome(f.nome);
            }} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar...</option>
              {(funcionarios || []).map(f => <option key={f.id} value={f.id}>{f.nome} {f.funcao ? `— ${f.funcao}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Responsável pela retirada *</label>
            <input value={responsavelNome} onChange={e => setResponsavelNome(e.target.value)} placeholder="Nome do responsável"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Observações</label>
            <input value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Ex: Utilizado na concretagem da Torre A"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </div>
      </div>

      {/* Scanner de barcode */}
      {obra && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Scan size={16} className="text-blue-500" /> Scanner / Busca de Material
          </h2>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={barcodeRef}
                value={barcodeInput}
                onChange={e => setBarcodeInput(e.target.value)}
                onKeyDown={onBarcodeKeyDown}
                placeholder="Escaneie o código ou busque por nome/código..."
                className="w-full pl-9 border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                autoComplete="off"
              />
            </div>
            <button onClick={() => buscarMaterial(barcodeInput)} disabled={buscando || !barcodeInput}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {buscando ? <Loader2 size={16} className="animate-spin" /> : 'Adicionar'}
            </button>
          </div>
          {erroBusca && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertTriangle size={12} />{erroBusca}</p>}
          <p className="text-xs text-gray-400 mt-2">Pressione <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Enter</kbd> após escanear ou digitar o código</p>
        </div>
      )}

      {/* Lista de itens */}
      {itens.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <span className="text-sm font-medium text-gray-700">{itens.length} item(s) na lista</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 border-b">
              <tr>
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Material</th>
                <th className="px-4 py-2 text-right">Disponível</th>
                <th className="px-4 py-2 text-right w-32">Quantidade *</th>
                <th className="px-4 py-2 text-center w-12"></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item, idx) => {
                const qtd = parseFloat(item.quantidade || 0);
                const insuf = qtd > item.saldo_disponivel;
                return (
                  <tr key={idx} className={`border-b border-gray-50 ${insuf ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{item.material_codigo}</td>
                    <td className="px-4 py-2 font-medium text-gray-800">{item.material_nome}</td>
                    <td className="px-4 py-2 text-right text-gray-500 font-mono">
                      <span className={item.saldo_disponivel <= 0 ? 'text-red-600' : ''}>{fmt(item.saldo_disponivel)} {item.unidade_sigla}</span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <input
                          type="number" min="0.01" step="0.01" value={item.quantidade}
                          onChange={e => setQtd(idx, e.target.value)}
                          className={`w-24 text-right border rounded-lg px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-400 ${insuf ? 'border-red-400 bg-red-50' : ''}`}
                        />
                        <span className="text-xs text-gray-400">{item.unidade_sigla}</span>
                      </div>
                      {insuf && <p className="text-xs text-red-600 text-right mt-0.5">Saldo insuficiente</p>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button onClick={() => removerItem(idx)} className="text-gray-400 hover:text-red-500">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {erroEnvio && <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm flex items-center gap-2"><AlertTriangle size={16} />{erroEnvio}</div>}

      {/* Confirm */}
      {itens.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={confirmarSaida}
            disabled={loading || !obra || !responsavelNome || !itens.length}
            className="px-8 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Registrando...</> : <><CheckCircle size={18} /> Confirmar Saída ({itens.length} itens)</>}
          </button>
        </div>
      )}
    </div>
  );
}
